import { ArrowDown, ChevronDown, ChevronUp } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiRequest, type TokenGetter } from "../api";
import type { FunnelRow } from "../types";
import {
  buildAlerts,
  formatRate,
  rate,
  STEPS,
  type DailyFunnel,
  type DashboardAlert,
  type FunnelSummary,
  type SyncState,
} from "./alerts";
import { buildPeriod, formatDay, formatRange, formatShortDay, listDays, todayJst, type Period, type PeriodKind } from "./period";
import "./overview.css";

interface DailyResponse {
  rows?: FunnelRow[];
  periodSummary?: Partial<FunnelSummary>;
}

interface KpiTarget {
  metric_key: string;
  period_start: string;
  period_end: string;
  target_value: number;
}

interface AlertStateRow {
  alert_key: string;
  updated_at: string;
  updated_by: string;
}

interface FollowSourceRow {
  key: string;
  kind: "article" | "post" | "link" | "unknown";
  channel: string;
  url: string | null;
  path: string | null;
  title: string | null;
  follows: number;
}

interface ChangeLogRow {
  id: string;
  article_path: string;
  kind: "memo" | "change" | "publish";
  occurred_on: string;
  body: string;
  title: string | null;
}

const EMPTY: FunnelSummary = { blogLandings: 0, blogToLpClicks: 0, lpViews: 0, lineCtaClicks: 0, lineFirstFollows: 0 };

function summarize(response: DailyResponse | null): { summary: FunnelSummary; daily: DailyFunnel[] } {
  const byDay = new Map<string, DailyFunnel>();
  for (const row of response?.rows ?? []) {
    const point = byDay.get(row.report_date) ?? { day: row.report_date, ...EMPTY };
    point.blogLandings += Number(row.blog_landings ?? 0);
    point.blogToLpClicks += Number(row.blog_to_lp_clicks ?? 0);
    point.lpViews += Number(row.lp_views ?? 0);
    point.lineCtaClicks += Number(row.line_cta_clicks ?? 0);
    point.lineFirstFollows += Number(row.line_first_follows ?? 0);
    byDay.set(row.report_date, point);
  }
  const daily = [...byDay.values()].sort((a, b) => a.day.localeCompare(b.day));
  // 期間の合計は、日別の足し算ではなく計測APIの重複を除いた値を使う。
  const fallback = daily.reduce((sum, point) => ({
    blogLandings: sum.blogLandings + point.blogLandings,
    blogToLpClicks: sum.blogToLpClicks + point.blogToLpClicks,
    lpViews: sum.lpViews + point.lpViews,
    lineCtaClicks: sum.lineCtaClicks + point.lineCtaClicks,
    lineFirstFollows: sum.lineFirstFollows + point.lineFirstFollows,
  }), { ...EMPTY });
  const period = response?.periodSummary;
  const summary = period ? {
    blogLandings: Number(period.blogLandings ?? 0),
    blogToLpClicks: Number(period.blogToLpClicks ?? 0),
    lpViews: Number(period.lpViews ?? 0),
    lineCtaClicks: Number(period.lineCtaClicks ?? 0),
    lineFirstFollows: Number(period.lineFirstFollows ?? 0),
  } : fallback;
  return { summary, daily };
}

function useOverviewData(getToken: TokenGetter, period: Period) {
  const [state, setState] = useState<{
    loading: boolean;
    measurementError: boolean;
    current: DailyResponse | null;
    previous: DailyResponse | null;
    targets: KpiTarget[];
    alertStates: AlertStateRow[];
    syncStates: SyncState[];
    sources: { items: FollowSourceRow[]; total: number; truncated: boolean } | null;
    sourcesError: boolean;
    changeLogs: ChangeLogRow[];
    loadedAt: Date | null;
  }>({ loading: true, measurementError: false, current: null, previous: null, targets: [], alertStates: [], syncStates: [], sources: null, sourcesError: false, changeLogs: [], loadedAt: null });

  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true }));
    const settle = <T,>(promise: Promise<T>) => promise.then((value) => ({ ok: true as const, value }), () => ({ ok: false as const, value: null }));
    const [current, previous, targets, alertStates, syncStates, sources, changeLogs] = await Promise.all([
      settle(apiRequest<DailyResponse>(getToken, `/api/measurement/daily?from=${period.from}&to=${period.to}`)),
      settle(apiRequest<DailyResponse>(getToken, `/api/measurement/daily?from=${period.compareFrom}&to=${period.compareTo}`)),
      settle(apiRequest<{ items: KpiTarget[] }>(getToken, "/api/marketing/kpi-targets")),
      settle(apiRequest<{ items: AlertStateRow[] }>(getToken, "/api/marketing/alert-states")),
      settle(apiRequest<{ items: SyncState[] }>(getToken, "/api/marketing/sync-status")),
      settle(apiRequest<{ items: FollowSourceRow[]; total: number; truncated: boolean }>(getToken, `/api/marketing/follow-sources?from=${period.from}&to=${period.to}`)),
      settle(apiRequest<{ items: ChangeLogRow[] }>(getToken, `/api/marketing/change-logs?from=${period.compareFrom}&to=${period.to}`)),
    ]);
    setState({
      loading: false,
      measurementError: !current.ok || !previous.ok,
      current: current.value,
      previous: previous.value,
      targets: targets.value?.items ?? [],
      alertStates: alertStates.value?.items ?? [],
      syncStates: syncStates.value?.items ?? [],
      sources: sources.value,
      sourcesError: !sources.ok,
      changeLogs: changeLogs.value?.items ?? [],
      loadedAt: new Date(),
    });
  }, [getToken, period.compareFrom, period.compareTo, period.from, period.to]);

  useEffect(() => { void load(); }, [load]);
  return { ...state, reload: load };
}

export function MarketingOverview({ getToken, canWrite }: { getToken: TokenGetter; canWrite: boolean }) {
  const today = todayJst();
  const [kind, setKind] = useState<PeriodKind>("month");
  const [custom, setCustom] = useState({ from: `${today.slice(0, 7)}-01`, to: today });
  const period = useMemo(() => buildPeriod(kind, today, custom), [custom, kind, today]);
  const data = useOverviewData(getToken, period);
  const current = useMemo(() => summarize(data.current), [data.current]);
  const previous = useMemo(() => summarize(data.previous), [data.previous]);
  const alerts = useMemo(() => data.loading || data.measurementError ? [] : buildAlerts({
    period, today, current: current.summary, previous: previous.summary, daily: current.daily, syncStates: data.syncStates,
  }), [current, data.loading, data.measurementError, data.syncStates, period, previous.summary, today]);
  const target = data.targets.find((item) => item.metric_key === "line_first_follows" && item.period_start === `${period.from.slice(0, 7)}-01` && item.period_end.slice(0, 7) === period.from.slice(0, 7) && period.kind !== "custom");

  const updatedAt = data.loadedAt ? `${String(data.loadedAt.getHours()).padStart(2, "0")}:${String(data.loadedAt.getMinutes()).padStart(2, "0")} に更新` : "読み込み中";
  const progress = period.kind === "month" ? `（${period.daysTotal}日中${period.daysElapsed}日目）` : "";

  return <>
    <header className="page-header overview-header">
      <div>
        <h1 tabIndex={-1}>まめぶろぐの集客</h1>
        <p className="muted">{formatRange(period.from, period.to)}{progress}・{updatedAt}</p>
      </div>
      <div className="period-switch" role="group" aria-label="期間">
        {([["month", "今月"], ["lastMonth", "先月"], ["custom", "期間を指定"]] as const).map(([value, label]) => (
          <button key={value} className={kind === value ? "active" : ""} aria-pressed={kind === value} onClick={() => setKind(value)}>{label}</button>
        ))}
      </div>
    </header>
    {kind === "custom" && (
      <div className="period-custom">
        <label>開始日<input className="input" type="date" value={custom.from} max={custom.to} onChange={(event) => setCustom({ ...custom, from: event.target.value })} /></label>
        <label>終了日<input className="input" type="date" value={custom.to} min={custom.from} max={today} onChange={(event) => setCustom({ ...custom, to: event.target.value })} /></label>
      </div>
    )}
    {data.measurementError && <p className="error-banner">計測APIから数字を取得できませんでした。時間をおいて再読み込みしてください。</p>}

    <TodayChecks
      alerts={alerts}
      loading={data.loading}
      resolved={data.alertStates}
      canWrite={canWrite}
      getToken={getToken}
      onChanged={data.reload}
      period={period}
      daily={current.daily}
      changeLogs={data.changeLogs}
    />

    <div className="overview-row">
      <FriendsCard period={period} follows={current.summary.lineFirstFollows} previousFollows={previous.summary.lineFirstFollows} target={target?.target_value ?? null} loading={data.loading} />
      <DropoffCard current={current.summary} previous={previous.summary} loading={data.loading} comparedLabel={period.kind === "month" ? "先月" : "前の期間"} />
    </div>

    <SourcesCard sources={data.sources} error={data.sourcesError} loading={data.loading} />
  </>;
}

const SEVERITY_LABEL: Record<DashboardAlert["severity"], string> = { problem: "要対応", warning: "確認", good: "良い兆し" };

function TodayChecks({ alerts, loading, resolved, canWrite, getToken, onChanged, period, daily, changeLogs }: {
  alerts: DashboardAlert[];
  loading: boolean;
  resolved: AlertStateRow[];
  canWrite: boolean;
  getToken: TokenGetter;
  onChanged: () => Promise<void>;
  period: Period;
  daily: DailyFunnel[];
  changeLogs: ChangeLogRow[];
}) {
  const [showResolved, setShowResolved] = useState(false);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const resolvedKeys = new Set(resolved.map((row) => row.alert_key));
  const open = alerts.filter((alert) => !resolvedKeys.has(alert.key));
  const done = alerts.filter((alert) => resolvedKeys.has(alert.key));
  const visible = showResolved ? done : open;
  const expanded = openKey ?? visible.find((alert) => alert.chart)?.key ?? null;

  async function setResolved(alert: DashboardAlert, value: boolean) {
    setSaving(alert.key); setError(null);
    try {
      await apiRequest(getToken, `/api/marketing/alert-states/${encodeURIComponent(alert.key)}`, value
        ? { method: "PUT", body: JSON.stringify({}) }
        : { method: "DELETE" });
      await onChanged();
    } catch {
      setError("状態を保存できませんでした。もう一度お試しください。");
    } finally {
      setSaving(null);
    }
  }

  return (
    <section className="panel checks" aria-labelledby="checks-title">
      <div className="card-head">
        <div>
          <h2 id="checks-title">{showResolved ? "対応済みの確認" : "今日の確認"}</h2>
          <p className="muted">{showResolved ? "この期間に対応済みにしたものです" : "友だち追加への影響が大きい順に並べています"}</p>
        </div>
        {(done.length > 0 || showResolved) && (
          <button className="link-button" onClick={() => { setShowResolved(!showResolved); setOpenKey(null); }}>
            {showResolved ? "今日の確認に戻る" : `対応済み ${done.length}件を見る`} →
          </button>
        )}
      </div>
      {error && <p className="error-banner">{error}</p>}
      {loading && <p className="checks-empty">読み込み中です。</p>}
      {!loading && visible.length === 0 && (
        <p className="checks-empty">{showResolved ? "対応済みにしたものはありません。" : "いま確認が必要なものはありません。"}</p>
      )}
      {!loading && visible.map((alert) => {
        const isOpen = expanded === alert.key && alert.chart !== null;
        return (
          <article key={alert.key} className="check-row">
            <div className="check-summary">
              <span className={`label label-${alert.severity}`}>{SEVERITY_LABEL[alert.severity]}</span>
              <div className="check-text">
                <h3>{alert.title}</h3>
                <p>{alert.detail}</p>
              </div>
              {alert.impact && (
                <p className={`check-impact ${alert.severity === "good" ? "is-good" : ""}`}><strong>{alert.impact.value}</strong><span>{alert.impact.caption}</span></p>
              )}
              {alert.chart && (
                <button className="link-button check-toggle" aria-expanded={isOpen} onClick={() => setOpenKey(isOpen ? "" : alert.key)}>
                  {isOpen ? <>閉じる<ChevronUp size={15} aria-hidden="true" /></> : <>詳しく<ChevronDown size={15} aria-hidden="true" /></>}
                </button>
              )}
            </div>
            {isOpen && <AlertDetail alert={alert} period={period} daily={daily} changeLogs={changeLogs} />}
            {canWrite && (isOpen || !alert.chart) && (
              <div className="check-actions">
                {showResolved
                  ? <button className="button" disabled={saving === alert.key} onClick={() => setResolved(alert, false)}>未対応に戻す</button>
                  : <button className="button button-primary" disabled={saving === alert.key} onClick={() => setResolved(alert, true)}>{saving === alert.key ? "保存中…" : "対応済みにする"}</button>}
              </div>
            )}
          </article>
        );
      })}
    </section>
  );
}

function AlertDetail({ alert, period, daily, changeLogs }: { alert: DashboardAlert; period: Period; daily: DailyFunnel[]; changeLogs: ChangeLogRow[] }) {
  if (!alert.chart) return null;
  const byDay = new Map(daily.map((point) => [point.day, point]));
  const flags = changeLogs.filter((log) => log.kind !== "memo" && log.occurred_on >= period.from && log.occurred_on <= period.to);
  if (alert.chart.kind === "step") {
    const step = STEPS.find((item) => item.key === (alert.chart as { step: string }).step)!;
    const points = listDays(period.from, period.to).map((day) => {
      const point = byDay.get(day);
      return { day, value: point ? rate(point[step.to], point[step.from]) : null };
    });
    return (
      <div className="check-detail">
        <div className="detail-chart">
          <div className="detail-chart-head">
            <h4>{step.label}（日別）</h4>
            <p>線：この期間　点線：比べた期間の平均 {formatRate(alert.chart.previousRate)}</p>
          </div>
          <RateChart points={points} baseline={alert.chart.previousRate} flags={flags} from={period.from} to={period.kind === "month" ? `${period.from.slice(0, 8)}${String(period.daysTotal).padStart(2, "0")}` : period.to} />
          {flags.length === 0 && <p className="detail-note">この期間に記録された記事の変更はありません。変更を記録すると、グラフに印が付きます。</p>}
        </div>
      </div>
    );
  }
  const zeroDay = alert.chart.day;
  const from = listDays(period.from, zeroDay).slice(-14)[0] ?? period.from;
  const points = listDays(from, zeroDay).map((day) => ({ day, value: byDay.get(day)?.lineCtaClicks ?? 0 }));
  return (
    <div className="check-detail">
      <div className="detail-chart">
        <div className="detail-chart-head">
          <h4>LINEボタンのクリック（日別）</h4>
          <p>0件だった日を赤で示しています</p>
        </div>
        <CountBars points={points} highlight={zeroDay} />
      </div>
    </div>
  );
}

// グラフは表示幅に合わせて描く。viewBoxを拡大縮小すると、スマホで目盛りの文字が読めなくなるため。
function useElementWidth<T extends HTMLElement>(fallback: number) {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.max(Math.round(entry!.contentRect.width), 240)));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

function RateChart({ points, baseline, flags, from, to }: {
  points: Array<{ day: string; value: number | null }>;
  baseline: number;
  flags: ChangeLogRow[];
  from: string;
  to: string;
}) {
  const [ref, width] = useElementWidth<HTMLDivElement>(900);
  const height = width < 500 ? 160 : 200;
  const left = 44;
  const right = 12;
  const top = 12;
  const bottom = 24;
  const days = listDays(from, to);
  const values = points.flatMap((point) => point.value === null ? [] : [point.value]).concat(baseline);
  const max = Math.max(...values, 0.01) * 1.25;
  const x = (day: string) => left + (days.indexOf(day) / Math.max(days.length - 1, 1)) * (width - left - right);
  const y = (value: number) => top + (1 - value / max) * (height - top - bottom);
  const segments: string[] = [];
  let path = "";
  for (const point of points) {
    if (point.value === null) { if (path) segments.push(path); path = ""; continue; }
    path += `${path ? "L" : "M"}${x(point.day).toFixed(1)},${y(point.value).toFixed(1)}`;
  }
  if (path) segments.push(path);
  const ticks = [0, max / 2, max].map((value) => ({ value, label: `${(value * 100).toFixed(1)}%` }));
  const labels = [days[0], ...flags.map((flag) => flag.occurred_on), points.at(-1)?.day, days.at(-1)].filter((day, index, list): day is string => Boolean(day) && list.indexOf(day) === index);
  return (
    <div ref={ref} className="chart-frame">
    <svg className="line-chart" width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="日別の割合の推移">
      {ticks.map((tick) => (
        <g key={tick.value}>
          <line x1={left} x2={width - right} y1={y(tick.value)} y2={y(tick.value)} className="chart-grid" />
          <text x={left - 6} y={y(tick.value) + 4} textAnchor="end" className="chart-label">{tick.label}</text>
        </g>
      ))}
      <line x1={left} x2={width - right} y1={y(baseline)} y2={y(baseline)} className="chart-baseline" />
      {flags.map((flag, index) => {
        const flagX = x(flag.occurred_on);
        const text = `${formatShortDay(flag.occurred_on)} ${flag.body.length > 18 ? `${flag.body.slice(0, 18)}…` : flag.body}`;
        const labelWidth = text.length * 11 + 16;
        // 右端に近い印は、ラベルを線の左側に出す。
        const labelX = flagX + 6 + labelWidth > width - right ? flagX - 6 - labelWidth : flagX + 6;
        return (
          <g key={flag.id}>
            <line x1={flagX} x2={flagX} y1={top} y2={height - bottom} className="chart-flag" />
            <rect x={labelX} y={top + index * 26} width={labelWidth} height={22} rx={6} className="chart-flag-label" />
            <text x={labelX + 8} y={top + index * 26 + 15} className="chart-flag-text">{text}</text>
            <title>{`${formatDay(flag.occurred_on)} ${flag.title ?? flag.article_path}：${flag.body}`}</title>
          </g>
        );
      })}
      {segments.map((segment) => <path key={segment} d={segment} className="chart-line" />)}
      {labels.map((day) => <text key={day} x={x(day)} y={height - 6} textAnchor="middle" className="chart-label">{formatShortDay(day)}</text>)}
    </svg>
    </div>
  );
}

function CountBars({ points, highlight }: { points: Array<{ day: string; value: number }>; highlight: string }) {
  const [ref, width] = useElementWidth<HTMLDivElement>(900);
  const height = 180;
  const bottom = 22;
  const max = Math.max(...points.map((point) => point.value), 1);
  const slot = width / points.length;
  // 狭い画面では日付を1日おきに出す。
  const labelEvery = slot < 36 ? 2 : 1;
  return (
    <div ref={ref} className="chart-frame">
    <svg className="line-chart" width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="日別のクリック数">
      {points.map((point, index) => {
        const barHeight = (point.value / max) * (height - bottom - 16);
        return (
          <g key={point.day}>
            <rect x={index * slot + slot * 0.2} y={height - bottom - barHeight} width={slot * 0.6} height={Math.max(barHeight, 2)} rx={3} className={point.day === highlight ? "chart-bar is-alert" : "chart-bar"} />
            <text x={index * slot + slot / 2} y={height - bottom - barHeight - 6} textAnchor="middle" className={point.day === highlight ? "chart-value is-alert" : "chart-value"}>{point.value.toLocaleString()}</text>
            {(index % labelEvery === (points.length - 1) % labelEvery) && <text x={index * slot + slot / 2} y={height - 6} textAnchor="middle" className="chart-label">{formatShortDay(point.day)}</text>}
          </g>
        );
      })}
    </svg>
    </div>
  );
}

function FriendsCard({ period, follows, previousFollows, target, loading }: {
  period: Period;
  follows: number;
  previousFollows: number;
  target: number | null;
  loading: boolean;
}) {
  const radius = 90;
  const circumference = 2 * Math.PI * radius;
  const ratio = target ? Math.min(follows / target, 1) : 0;
  const expected = target ? Math.round((target * period.daysElapsed) / period.daysTotal) : null;
  const markerAngle = target && expected !== null ? (expected / target) * 360 - 90 : null;
  const pace = period.daysElapsed > 0 ? Math.round((follows / period.daysElapsed) * period.daysTotal) : 0;
  const remainingDays = period.daysTotal - period.daysElapsed;
  const perDay = target && remainingDays > 0 ? Math.max(Math.ceil((target - follows) / remainingDays), 0) : null;
  const gap = expected !== null ? follows - expected : null;
  const title = period.kind === "month" ? "今月の友だち追加" : period.kind === "lastMonth" ? "先月の友だち追加" : "この期間の友だち追加";

  return (
    <section className="panel friends-card" aria-labelledby="friends-title">
      <h2 id="friends-title">{title}</h2>
      <div className="friends-ring">
        <svg viewBox="0 0 220 220" aria-hidden="true">
          <circle cx="110" cy="110" r={radius} className="ring-track" />
          {target && <circle cx="110" cy="110" r={radius} className="ring-value" strokeDasharray={`${circumference * ratio} ${circumference}`} transform="rotate(-90 110 110)" />}
          {markerAngle !== null && !period.complete && (
            <line x1="110" y1="6" x2="110" y2="34" className="ring-marker" transform={`rotate(${markerAngle + 90} 110 110)`} />
          )}
        </svg>
        <div className="friends-ring-text">
          <strong>{loading ? "—" : follows.toLocaleString()}<small>人</small></strong>
          <span>{target ? `目標${target.toLocaleString()}人の${Math.round((follows / target) * 100)}%` : "目標は未設定です"}</span>
        </div>
      </div>
      {target && expected !== null && !period.complete && <p className="muted small">赤い印は、今日までに届いていたい目安（{expected.toLocaleString()}人）です。</p>}
      <dl className="stat-list">
        {period.kind === "month" && <div><dt>このペースなら月末に</dt><dd>{pace.toLocaleString()}人</dd></div>}
        {perDay !== null && <div><dt>目標まで、1日あたり</dt><dd>{perDay.toLocaleString()}人</dd></div>}
        {gap !== null && !period.complete && <div><dt>目安より</dt><dd className={gap < 0 ? "is-alert" : ""}>{Math.abs(gap).toLocaleString()}人 {gap < 0 ? "少ない" : "多い"}</dd></div>}
        {target && period.complete && <div><dt>目標との差</dt><dd className={follows < target ? "is-alert" : ""}>{Math.abs(follows - target).toLocaleString()}人 {follows < target ? "足りない" : "上回った"}</dd></div>}
        <div><dt>{period.kind === "month" ? "先月の同じ期間" : "前の期間"}</dt><dd>{previousFollows.toLocaleString()}人</dd></div>
      </dl>
    </section>
  );
}

const DROPOFF_STEPS: Array<{ key: keyof FunnelSummary; label: string }> = [
  { key: "blogLandings", label: "ブログを読んだ" },
  { key: "blogToLpClicks", label: "LPへ進んだ" },
  { key: "lpViews", label: "LPを見た" },
  { key: "lineCtaClicks", label: "LINEボタンを押した" },
  { key: "lineFirstFollows", label: "LINEの友だちになった" },
];
const DROPOFF_WIDTHS = [95, 83, 71, 62, 54];

function DropoffCard({ current, previous, loading, comparedLabel }: { current: FunnelSummary; previous: FunnelSummary; loading: boolean; comparedLabel: string }) {
  const transitions = DROPOFF_STEPS.slice(1).map((step, index) => {
    const from = DROPOFF_STEPS[index]!.key;
    const now = rate(current[step.key], current[from]);
    const before = rate(previous[step.key], previous[from]);
    return { now, before, drop: now !== null && before !== null && before > 0 ? (before - now) / before : null };
  });
  // 前の期間より最も大きく下がった段階だけを強調する。
  const worst = transitions.reduce((best, item, index) => (item.drop !== null && item.drop > 0.05 && (best === -1 || item.drop > transitions[best]!.drop!) ? index : best), -1);
  return (
    <section className="panel dropoff-card" aria-labelledby="dropoff-title">
      <div className="card-head">
        <div>
          <h2 id="dropoff-title">どこで離れているか</h2>
          <p className="muted">ブログを読んだ人が、LINEの友だちになるまでの流れです。</p>
        </div>
      </div>
      <ol className="dropoff">
        {DROPOFF_STEPS.map((step, index) => (
          <li key={step.key}>
            <div className={`dropoff-bar ${index === DROPOFF_STEPS.length - 1 ? "is-goal" : ""}`} style={{ width: `${DROPOFF_WIDTHS[index]}%` }}>
              <span>{step.label}</span>
              <strong>{loading ? "—" : `${current[step.key].toLocaleString()}人`}</strong>
            </div>
            {index < transitions.length && (
              <div className={`dropoff-rate ${worst === index ? "is-worst" : ""}`}>
                <ArrowDown size={16} strokeWidth={2.4} aria-hidden="true" />
                <span className="rate-pill">{formatRate(transitions[index]!.now)}が次へ</span>
                <span>{comparedLabel}は{formatRate(transitions[index]!.before)}{worst === index ? "。ここが一番下がっています" : ""}</span>
              </div>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

const CHANNEL_LABEL: Record<string, string> = {
  seo: "ブログ（検索から）",
  threads: "Threads",
  x: "X",
  instagram: "Instagram",
  direct: "直接",
  unknown: "流入元が不明",
};
const KIND_LABEL: Record<FollowSourceRow["kind"], string> = { article: "記事", post: "投稿", link: "リンク", unknown: "不明" };

function SourcesCard({ sources, error, loading }: { sources: { items: FollowSourceRow[]; total: number; truncated: boolean } | null; error: boolean; loading: boolean }) {
  const [showAll, setShowAll] = useState(false);
  const items = sources?.items ?? [];
  const total = items.reduce((sum, item) => sum + item.follows, 0);
  const max = Math.max(...items.map((item) => item.follows), 1);
  const visible = showAll ? items : items.slice(0, 5);
  return (
    <section className="panel sources-card" aria-labelledby="sources-title">
      <div className="card-head">
        <div>
          <h2 id="sources-title">どの記事・投稿から友だちになったか</h2>
          <p className="muted">友だちになった人が多い順{sources?.truncated ? "（新しい500人までで集計）" : ""}</p>
        </div>
        {items.length > 5 && <button className="link-button" onClick={() => setShowAll(!showAll)}>{showAll ? "上位5件に戻す" : `すべて見る（${items.length}件）`} →</button>}
      </div>
      {error && <p className="error-banner">流入元を取得できませんでした。</p>}
      {loading && <p className="checks-empty">読み込み中です。</p>}
      {!loading && !error && items.length === 0 && <p className="checks-empty">この期間に友だちになった人の記録はまだありません。LINEの受け取り処理が本番につながると表示されます。</p>}
      {!loading && visible.map((item) => (
        <div key={item.key} className="source-row">
          <span className={`label ${item.kind === "unknown" ? "label-muted" : "label-info"}`}>{KIND_LABEL[item.kind]}</span>
          <div className="source-name">
            {item.url
              ? <a href={item.url} target="_blank" rel="noreferrer">{item.title ?? item.path ?? item.url}</a>
              : <span>{item.title ?? "記録なし"}</span>}
            <small>{CHANNEL_LABEL[item.channel] ?? item.channel}</small>
          </div>
          <div className="share-bar" aria-hidden="true"><span style={{ width: `${(item.follows / max) * 100}%` }} /></div>
          <p className="source-count"><strong>{item.follows.toLocaleString()}</strong>人</p>
          <p className="source-share">全体の{total > 0 ? Math.round((item.follows / total) * 100) : 0}%</p>
        </div>
      ))}
    </section>
  );
}
